import { Link, useNavigate } from "@remix-run/react";
import { useTranslation } from "react-i18next";

import { Icon } from "~/components/Icon";
import { Button } from "~/components/ui/button";
import { useMediaQuery } from "~/hooks/useMediaQuery";

import { SearchBadge } from "../SearchBadge";

import type { FC } from "react";
import type { SearchResponse } from "~/api/generated-api";

type BadgeVariant =
  | "polish"
  | "math"
  | "english"
  | "7class"
  | "8class"
  | "8class-review"
  | null
  | undefined;

interface SearchResultProps {
  result: SearchResponse["data"][number];
  getBadgeVariant: (value: string) => BadgeVariant;
  handleCloseModal: (lessonId: string) => void;
}

export const SearchResult: FC<SearchResultProps> = ({
  result,
  getBadgeVariant,
  handleCloseModal,
}) => {
  const { t } = useTranslation();
  const isLargeScreen = useMediaQuery({ minWidth: 1024 });
  const navigate = useNavigate();

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onClick={(event) => {
        if (isLargeScreen) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        navigate(
          result.isEnrolled
            ? `/course/${result.courseId}/lesson/${result.id}`
            : `/courses?schoolClassId=${result.schoolClassId}&subjectId=${result.subjectId}&lessonId=${result.id}`,
        );
        handleCloseModal(result.id);
      }}
      key={result.id}
      className="col-span-2 flex gap-x-3 rounded-3xl border border-[#e2e8f0] bg-white p-4 hover:border-black hover:bg-violet-50 xl:p-6"
    >
      <div className="relative aspect-[16/9] h-fit w-full max-w-[117px] shrink-0 overflow-hidden rounded-xl xl:max-w-[189px]">
        <img
          src={result.vimeoThumbnailUrl || "https://placehold.co/189x107"}
          alt={result.lessonTitle}
          className="aspect-[16/9] w-full max-w-[117px] shrink-0 rounded-lg xl:max-w-[189px]"
        />
        {!result.isEnrolled && result.isFreemium && (
          <div className="absolute bottom-0 right-0 grid w-full place-items-center rounded-lg bg-success-600 py-[3px]">
            <Icon name="FREERIGHT" className="size-4 shrink-0 text-white" />
          </div>
        )}
      </div>
      <div className="ml-1 w-full xl:ml-3">
        <small className="xl:details sr-only xl:not-sr-only xl:text-neutral-800">
          {result.courseName} • {t("searchResults.lesson")} {result.lessonDisplayOrder}{" "}
          {t("searchResults.of")} {result.courseLessonCount}
        </small>
        <hgroup>
          <h3
            className="h7 xl:h6 w-full text-neutral-950"
            dangerouslySetInnerHTML={{ __html: result.lessonTitle }}
          />
          <p
            className="xl:details sr-only xl:not-sr-only xl:mt-1 xl:line-clamp-1 xl:text-ellipsis xl:text-neutral-800"
            dangerouslySetInnerHTML={{ __html: result.description ?? "" }}
          />
        </hgroup>
        <div className="mt-3 flex flex-wrap gap-2">
          {result.schoolClassName && (
            <SearchBadge variant={getBadgeVariant(result.schoolClassName)} />
          )}
          {result.subjectName && <SearchBadge variant={getBadgeVariant(result.subjectName)} />}
        </div>
      </div>
      <div className="flex flex-col gap-y-4">
        <Button variant="secondary" className="hidden h-min w-max shrink-0 lg:block" asChild>
          {!result.isEnrolled ? (
            <Link
              to={`/courses?schoolClassId=${result.schoolClassId}&subjectId=${result.subjectId}&lessonId=${result.id}`}
              onClick={() => handleCloseModal(result.id)}
            >
              {t("searchResults.goToSubject")}
            </Link>
          ) : (
            <Link
              to={`/course/${result.courseId}/lesson/${result.id}?schoolClassId=${result.schoolClassId}&subjectId=${result.subjectId}`}
              onClick={() => handleCloseModal(result.id)}
            >
              {t("searchResults.goToLesson")}
            </Link>
          )}
        </Button>
        {!result.isEnrolled && result.isFreemium && (
          <div className="body-sm-md hidden w-max shrink-0 items-center gap-x-1 rounded-lg bg-success-600 px-2.5 text-white lg:flex">
            <Icon name="FREERIGHT" className="size-4 shrink-0 text-white" />{" "}
            {t("searchResults.freeTrialLesson")}
          </div>
        )}
      </div>
    </div>
  );
};
